<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const dialogEl = ref<HTMLElement | null>(null);
const titleId = `form-dialog-title-${Math.random().toString(36).slice(2)}`;
let previouslyFocused: HTMLElement | null = null;

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    classicTheme?: boolean;
    title: string;
    message?: string;
    submitLabel?: string;
    submitDisabled?: boolean;
    submitVariant?: "primary" | "danger";
    scrollable?: boolean;
    hideCancel?: boolean;
    noActions?: boolean;
    closeOnBackdrop?: boolean;
    closeDisabled?: boolean;
  }>(),
  {
    submitLabel: undefined,
    submitVariant: "primary",
    closeOnBackdrop: true,
    closeDisabled: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "submit"): void;
}>();

function close() {
  if (props.closeDisabled) return;
  emit("update:modelValue", false);
}

function submit() {
  if (props.submitDisabled) return;
  emit("submit");
}

function focusableElements() {
  return dialogEl.value
    ? Array.from(
        dialogEl.value.querySelectorAll<HTMLElement>(focusableSelector),
      ).filter((element) => !element.hidden)
    : [];
}

async function focusDialog() {
  await nextTick();
  if (!props.modelValue || !dialogEl.value) return;
  const autofocusTarget = dialogEl.value.querySelector<HTMLElement>(
    "[autofocus]:not([disabled])",
  );
  (autofocusTarget ?? focusableElements()[0] ?? dialogEl.value).focus();
}

function restoreFocus() {
  const target = previouslyFocused;
  previouslyFocused = null;
  void nextTick(() => {
    if (target?.isConnected) target.focus();
  });
}

function onKeydown(e: KeyboardEvent) {
  if (!props.modelValue) return;
  if (e.key === "Escape") {
    close();
    return;
  }
  if (e.key !== "Tab" || !dialogEl.value) return;

  const focusable = focusableElements();
  if (!focusable.length) {
    e.preventDefault();
    dialogEl.value.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (e.shiftKey && (active === first || !dialogEl.value.contains(active))) {
    e.preventDefault();
    last.focus();
  } else if (
    !e.shiftKey &&
    (active === last || !dialogEl.value.contains(active))
  ) {
    e.preventDefault();
    first.focus();
  }
}

watch(
  () => props.modelValue,
  (open, wasOpen) => {
    if (open) {
      previouslyFocused = document.activeElement as HTMLElement | null;
      void focusDialog();
    } else if (wasOpen) {
      restoreFocus();
    }
  },
  { immediate: true, flush: "post" },
);

onMounted(() => document.addEventListener("keydown", onKeydown));
onUnmounted(() => {
  document.removeEventListener("keydown", onKeydown);
  if (props.modelValue) restoreFocus();
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      :class="{ 'classic-editor-shared-controls': classicTheme }"
      class="tw:font-sans tw:fixed tw:inset-0 tw:z-[9999] tw:flex tw:items-center tw:justify-center"
      @click="closeOnBackdrop ? close() : null"
    >
      <div class="tw:absolute tw:inset-0 tw:bg-black/50" />
      <div
        ref="dialogEl"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        tabindex="-1"
        class="tw:relative tw:bg-surface tw:rounded-xl tw:shadow-2xl tw:p-6 tw:w-80"
        :class="scrollable ? 'tw:max-h-[70vh] tw:flex tw:flex-col' : ''"
        @click.stop
      >
        <h3
          :id="titleId"
          class="tw:m-0 tw:text-sm tw:font-semibold tw:text-label tw:mb-3"
        >
          {{ title }}
        </h3>

        <p
          v-if="message"
          class="tw:m-0 tw:text-sm tw:text-label-tertiary tw:mb-4"
        >
          {{ message }}
        </p>

        <div
          :class="
            scrollable
              ? 'tw:flex-1 tw:overflow-y-auto tw:overflow-x-hidden tw:min-h-0 tw:mb-4'
              : ''
          "
        >
          <slot />
        </div>

        <div
          v-if="!noActions"
          class="tw:flex tw:justify-end tw:gap-2"
          :class="!message && !scrollable ? 'tw:mt-4' : ''"
        >
          <button
            v-if="!hideCancel"
            data-test="dialog-cancel"
            :disabled="closeDisabled"
            :aria-disabled="closeDisabled ? 'true' : undefined"
            class="tw:px-3 tw:py-1.5 tw:text-sm tw:border tw:border-separator-strong tw:rounded-lg tw:bg-surface tw:text-label-secondary tw:hover:bg-hover tw:cursor-pointer tw:transition-colors"
            :class="closeDisabled ? 'tw:opacity-50 tw:pointer-events-none' : ''"
            @click="close"
          >
            {{ t("sharedUi.common.cancel") }}
          </button>
          <button
            data-test="dialog-submit"
            :disabled="submitDisabled"
            :aria-disabled="submitDisabled ? 'true' : undefined"
            class="tw:px-3 tw:py-1.5 tw:text-sm tw:border-0 tw:rounded-lg tw:text-white tw:cursor-pointer tw:transition-colors"
            :class="[
              submitVariant === 'danger'
                ? 'tw:bg-destructive tw:hover:bg-destructive-hover'
                : 'tw:bg-pv-turquoise-500 tw:hover:bg-pv-turquoise-600',
              submitDisabled ? 'tw:opacity-50 tw:pointer-events-none' : '',
            ]"
            @click="submit"
          >
            {{ submitLabel ?? t("sharedUi.common.save") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
